import apiClient from '@skygroup/shared/API/apiClient';
import { BotSession, BotSessionContext, CheckoutPayload } from '../types/bot.types';
import { asNewSessionLimitError } from '../utils/botSessionLimit';

const BASE = '/ms/bot-agent';

/**
 * Creates the session row. Rejects with a `BotNewSessionLimitError` when the
 * per-IP new-session quota is hit, so the widget can show a countdown instead of
 * failing silently.
 *
 * `suppressErrorToast` opts this one call out of apiClient's global
 * "403 - Forbidden" toast — the widget renders its own notice for every failure
 * mode here, and the raw toast on top of it is pure noise.
 */
export async function createSession(payload: {
  app: 'website';
  website_id?: number;
  /** FK to tbl_agent_users.id — set for logged-in B2B agents. */
  agent_user_id?: number;
  membership_user_id?: number;
  anonymous_id?: string;
}): Promise<BotSession> {
  try {
    const res = await apiClient.post(`${BASE}/sessions`, payload, {
      suppressErrorToast: true,
    });
    return res.data.data;
  } catch (e) {
    throw asNewSessionLimitError(e) ?? e;
  }
}

export async function loadSession(sessionId: string): Promise<BotSession> {
  const res = await apiClient.get(`${BASE}/sessions/${sessionId}`);
  return res.data.data;
}

export async function updateSessionContext(sessionId: string, context: Partial<BotSessionContext>): Promise<void> {
  await apiClient.patch(`${BASE}/sessions/${sessionId}/context`, { context });
}

export async function getCheckoutPayload(sessionId: string): Promise<CheckoutPayload> {
  const res = await apiClient.get(`${BASE}/sessions/${sessionId}/checkout-payload`);
  return res.data.data;
}

/** Remove one cart item via PATCH context (works on all deployed MS builds). */
export async function removeCartItem(
  sessionId: string,
  cartItemId: string,
): Promise<CheckoutPayload> {
  const session = await loadSession(sessionId);
  const current = session.context?.cart ?? [];
  const next = current.filter((c) => c.cart_item_id !== cartItemId);
  if (next.length === current.length) {
    const err = new Error(`No cart item with id ${cartItemId}`);
    (err as { response?: { status: number } }).response = { status: 404 };
    throw err;
  }
  await updateSessionContext(sessionId, { cart: next });
  return getCheckoutPayload(sessionId);
}

/**
 * Full assembled ShoppingCartV2 for the session cart — built by the BE
 * BotCartBuilderService. This is what the web checkout flow writes to
 * localStorage.cartV2[uuid] and renders at /checkout/:uuid.
 * Returned shape matches the FE ShoppingCartV2 (see ShoppingCartSliceV2).
 *
 * `suppressErrorToast` (see createSession above): useBotCheckoutHandler renders
 * its own translated "checkout failed" toast for every failure mode here, and a
 * raw "500 - Internal Server Error" stacked on top of it is pure noise.
 */
export async function getCheckoutCart(
  sessionId: string,
  token?: string,
): Promise<any> {
  // WhatsApp deep-links carry a signed `token` the BE verifies. The web CTA
  // path is same-session and omits it.
  const qs = token ? `?token=${encodeURIComponent(token)}` : '';
  const res = await apiClient.get(
    `${BASE}/sessions/${sessionId}/checkout-cart${qs}`,
    { suppressErrorToast: true },
  );
  return res.data.data;
}

export async function archiveSession(sessionId: string): Promise<void> {
  await apiClient.patch(`${BASE}/sessions/${sessionId}/archive`);
}

export interface WhatsappHandoffResponse {
  wa_url: string;
  expires_at: number;
}

/**
 * Mints a wa.me URL with a prefilled "Resume #<token>" body. When the user
 * sends it from the WhatsApp app, our SmartSend webhook detects the token
 * and migrates this same session into the WhatsApp channel.
 */
export async function requestWhatsappHandoff(
  sessionId: string,
): Promise<WhatsappHandoffResponse> {
  const res = await apiClient.post(
    `${BASE}/sessions/${sessionId}/handoff/whatsapp`,
  );
  return res.data.data;
}
